/**
 * The app shell. UF_00 section 4, the navigation model, expanded for the super-app architecture.
 *
 * The platform is now ONE application a single verified identity moves through as owner, investor,
 * borrower, merchant or business, never a set of separate logins. The navigation reflects that: a
 * grouped rail on desktop, a thumb-reachable tab bar on mobile with the deeper areas behind "More".
 *
 *   MONEY     Wallet, Pay
 *   INVEST    Marketplace, Portfolio, Tokenize
 *   BORROW    Credit
 *   ACCOUNT   Support, Settings
 *
 * Same information architecture, laid out for the form factor (UF_00 3.3 principle 5), and the reason
 * there is ONE component here rather than a mobile app and a web app that drift.
 *
 * ## Why Pay is the centre on mobile and why it is bigger
 *
 * It is the tab that moves money. Everything else exists to make that one action safe: the wallet
 * holds value, the marketplace and portfolio put it to work, credit lends against it, the profile
 * proves who you are. Emphasising it is the information architecture admitting what the app is for.
 */

import { useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { TierBadge } from './chrome';
import { Sheet } from './overlay';
import { ThemeToggle } from './theme-toggle';
import { api } from '@/lib/api';
import { useSession } from '@/lib/session-context';

type Icon = (props: { className?: string }) => ReactElement;

interface Destination {
  href: string;
  label: string;
  icon: Icon;
}

interface NavGroup {
  /** A section heading on the desktop rail. Omitted for the lone Home item. */
  label?: string;
  items: Destination[];
}

// ============================================================================
// Icons. One stroke set, 1.8 weight, 24 viewBox, matching across the whole app.
// ============================================================================

const HomeIcon: Icon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      d="M3 10.5 12 3l9 7.5M5.25 9.75V20a1 1 0 0 0 1 1H9.5v-5.25h5V21h3.25a1 1 0 0 0 1-1V9.75"
    />
  </svg>
);
const WalletIcon: Icon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      d="M3 8.25A2.25 2.25 0 0 1 5.25 6h13.5A2.25 2.25 0 0 1 21 8.25v9A2.25 2.25 0 0 1 18.75 19.5H5.25A2.25 2.25 0 0 1 3 17.25v-9Zm0 3h18M16.5 14.25h1.5"
    />
  </svg>
);
const PayIcon: Icon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      d="M7.5 7.5h11m0 0-3-3m3 3-3 3M16.5 16.5h-11m0 0 3 3m-3-3 3-3"
    />
  </svg>
);
const MarketIcon: Icon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      d="M4 9V6.5A1.5 1.5 0 0 1 5.5 5h13A1.5 1.5 0 0 1 20 6.5V9M4 9l-.7 2.1a2 2 0 0 0 1.9 2.65A2.1 2.1 0 0 0 7.3 12M4 9h16m0 0 .7 2.1a2 2 0 0 1-1.9 2.65A2.1 2.1 0 0 1 16.7 12m-9.4 0a2.1 2.1 0 0 0 4.2 0m0 0V9m0 3a2.1 2.1 0 0 0 4.2 0M6 14v5.5A1.5 1.5 0 0 0 7.5 21h9a1.5 1.5 0 0 0 1.5-1.5V14"
    />
  </svg>
);
const PortfolioIcon: Icon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      d="M4 20h16M6.5 20v-6M11 20V9m4.5 11v-8M20 20V5"
    />
  </svg>
);
const TokenizeIcon: Icon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      d="M4 9.5 12 4l8 5.5M5.5 8.75V19a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V8.75M9.5 20v-6h5v6"
    />
  </svg>
);
const CreditIcon: Icon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      d="M12 3 3 7.5v1.5h18V7.5L12 3ZM5.25 11.25v6M9.75 11.25v6M14.25 11.25v6M18.75 11.25v6M3 20.25h18"
    />
  </svg>
);
const SupportIcon: Icon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      d="M8 10.5h8M8 14h5m-8.5 5.5 2.2-2.2A2 2 0 0 1 8.1 16.7H17a2.5 2.5 0 0 0 2.5-2.5v-6A2.5 2.5 0 0 0 17 5.7H7A2.5 2.5 0 0 0 4.5 8.2v11.3Z"
    />
  </svg>
);
const SettingsIcon: Icon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      d="M10.3 3.6a1 1 0 0 1 1-.85h1.4a1 1 0 0 1 1 .85l.2 1.4a6.5 6.5 0 0 1 1.7 1l1.3-.55a1 1 0 0 1 1.25.43l.7 1.2a1 1 0 0 1-.23 1.3l-1.1.85a6.6 6.6 0 0 1 0 2l1.1.85a1 1 0 0 1 .23 1.3l-.7 1.2a1 1 0 0 1-1.25.43l-1.3-.55a6.5 6.5 0 0 1-1.7 1l-.2 1.4a1 1 0 0 1-1 .85h-1.4a1 1 0 0 1-1-.85l-.2-1.4a6.5 6.5 0 0 1-1.7-1l-1.3.55a1 1 0 0 1-1.25-.43l-.7-1.2a1 1 0 0 1 .23-1.3l1.1-.85a6.6 6.6 0 0 1 0-2l-1.1-.85a1 1 0 0 1-.23-1.3l.7-1.2a1 1 0 0 1 1.25-.43l1.3.55a6.5 6.5 0 0 1 1.7-1l.2-1.4Z"
    />
    <circle cx="12" cy="12" r="2.5" strokeWidth={1.8} />
  </svg>
);
const ProfileIcon: Icon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      d="M15.75 8.25a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.5 20.25a7.5 7.5 0 0 1 15 0"
    />
  </svg>
);
const MoreIcon: Icon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      d="M4 7h16M4 12h16M4 17h16"
    />
  </svg>
);
const WatchIcon: Icon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      d="M12 20s-7-4.35-9.2-8.5C1.3 8.6 2.6 5.5 5.7 5.5c1.9 0 3.2 1.1 4.3 2.6 1.1-1.5 2.4-2.6 4.3-2.6 3.1 0 4.4 3.1 2.9 6-2.2 4.15-9.2 8.5-9.2 8.5Z"
    />
  </svg>
);
const EarningsIcon: Icon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      d="M12 6v12m3-9.5c0-1.4-1.3-2.5-3-2.5S9 7.1 9 8.5 10.3 11 12 11s3 1.1 3 2.5S13.7 16 12 16s-3-1.1-3-2.5"
    />
  </svg>
);
const TradeIcon: Icon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      d="M4 17V7m0 10 3-3m-3 3-3-3M20 7v10m0-10 3 3m-3-3-3 3M9 9h6M9 15h6"
    />
  </svg>
);
const TrustIcon: Icon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      d="M12 3 4 6v5c0 4.5 3.2 8 8 10 4.8-2 8-5.5 8-10V6l-8-3Zm0 6v3m0 3h.01"
    />
  </svg>
);
const P2PIcon: Icon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      d="M9 7.5a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0Zm11 0a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0ZM3 19v-1a3.5 3.5 0 0 1 7 0v1M14 19v-1a3.5 3.5 0 0 1 7 0v1"
    />
  </svg>
);
const BusinessIcon: Icon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      d="M4 21V6a1 1 0 0 1 1-1h6v16M11 21h8a1 1 0 0 0 1-1v-9a1 1 0 0 0-1-1h-4M7 9h1m-1 4h1m6 0h1m-1 4h1"
    />
  </svg>
);
const MerchantIcon: Icon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h3m3 0v3m0 3h-6v-3"
    />
  </svg>
);
const BellIcon: Icon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      d="M14.857 17.082a24 24 0 0 0 3.844-.815 1.5 1.5 0 0 0 .5-2.6A5 5 0 0 1 18 10V9A6 6 0 0 0 6 9v1a5 5 0 0 1-1.2 3.667 1.5 1.5 0 0 0 .5 2.6c1.255.337 2.54.61 3.845.815m6.512 0a3 3 0 0 1-6.512 0m6.512 0a24.2 24.2 0 0 1-6.512 0"
    />
  </svg>
);

// ============================================================================
// Destinations
// ============================================================================

const HOME: Destination = { href: '/home', label: 'Home', icon: HomeIcon };
const WALLET: Destination = { href: '/wallet', label: 'Wallet', icon: WalletIcon };
const PAY: Destination = { href: '/transact', label: 'Pay', icon: PayIcon };
const MARKETPLACE: Destination = { href: '/marketplace', label: 'Marketplace', icon: MarketIcon };
const PORTFOLIO: Destination = { href: '/portfolio', label: 'Portfolio', icon: PortfolioIcon };
const TOKENIZE: Destination = { href: '/tokenize', label: 'Tokenize', icon: TokenizeIcon };
const CREDIT: Destination = { href: '/credit', label: 'Credit', icon: CreditIcon };
const SUPPORT: Destination = { href: '/support', label: 'Support', icon: SupportIcon };
const SETTINGS: Destination = { href: '/settings', label: 'Settings', icon: SettingsIcon };
const PROFILE: Destination = { href: '/profile', label: 'Profile', icon: ProfileIcon };
const NOTIFICATIONS: Destination = {
  href: '/notifications',
  label: 'Notifications',
  icon: BellIcon,
};
const WATCHLIST: Destination = { href: '/watchlist', label: 'Watchlist', icon: WatchIcon };
const EARNINGS: Destination = { href: '/earnings', label: 'Earnings', icon: EarningsIcon };
const MARKET: Destination = { href: '/market', label: 'Secondary market', icon: TradeIcon };
const TRUST: Destination = { href: '/trust', label: 'Trusts', icon: TrustIcon };
const BUSINESS: Destination = { href: '/business', label: 'Business', icon: BusinessIcon };
const MERCHANT: Destination = { href: '/merchant', label: 'Merchant', icon: MerchantIcon };
const LENDER: Destination = { href: '/lender', label: 'Lending', icon: EarningsIcon };
const P2P: Destination = { href: '/p2p', label: 'P2P trades', icon: P2PIcon };
// The second P2P flow. Named for what distinguishes it from the offer book (it is instant and the
// counterparty is us), not for the mechanism, which the participant does not need to care about.
const SWAP: Destination = { href: '/swap', label: 'Instant swap', icon: P2PIcon };

/** The desktop rail, grouped.
 *
 * NOTE: the retail app is for INVESTORS and SPONSORS. It does NOT manage trusts. A trust is a separate
 * ISSUER organisation with its own portal: it receives a sponsor's tokenization request, values the
 * asset, issues the tokens, and publishes the listing. So `Tokenize` here is the sponsor SUBMITTING a
 * property to the trust and tracking it; the trust itself is only ever VIEWED read-only from a listing.
 * See memory: trust-is-issuer-principal. `TRUST` is kept as a constant for that read-only view but is
 * deliberately not a nav destination in the retail app.
 */
const NAV: NavGroup[] = [
  { items: [HOME] },
  { label: 'Money', items: [WALLET, PAY, P2P, SWAP] },
  { label: 'Invest', items: [MARKETPLACE, PORTFOLIO, EARNINGS, WATCHLIST, MARKET] },
  { label: 'Own', items: [TOKENIZE] },
  { label: 'Borrow', items: [CREDIT] },
  { label: 'Account', items: [SUPPORT, SETTINGS] },
];

/** The five mobile tabs. Pay is the raised centre. The rest live behind More. */
const MOBILE_TABS: Destination[] = [HOME, WALLET, PAY, MARKETPLACE];
const MORE_ITEMS: Destination[] = [
  PORTFOLIO,
  EARNINGS,
  WATCHLIST,
  MARKET,
  P2P,
  TOKENIZE,
  CREDIT,
  LENDER,
  MERCHANT,
  BUSINESS,
  SUPPORT,
  NOTIFICATIONS,
  SETTINGS,
  PROFILE,
];
void TRUST;

function isActive(pathname: string, href: string): boolean {
  return pathname === href || pathname.startsWith(`${href}/`);
}

// ============================================================================
// Top bar
// ============================================================================

/**
 * The top bar. UF_00 section 4.3: wordmark, TIER BADGE, and the notification bell with unread count.
 *
 * The tier badge is not an ornament. UF_00 section 3.3: "compliance is visible, not hidden." A
 * participant who cannot see their own verification level has to discover it by being refused.
 */
function TopBar() {
  const { tier, loading, signedIn } = useSession();
  const [unread, setUnread] = useState(0);
  const { pathname } = useLocation();

  useEffect(() => {
    if (!signedIn) return;
    void api
      .unreadCount()
      .then((r) => setUnread(r.unread))
      .catch(() => setUnread(0));
  }, [signedIn, pathname]);

  return (
    <header className="sticky top-0 z-30 border-b border-border bg-surface/90 shadow-card backdrop-blur">
      <div className="flex h-topbar items-center gap-3 px-4 lg:px-8 2xl:px-12">
        <Link
          to="/home"
          className="flex items-center gap-2 rounded-field focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary text-sm font-bold text-white">
            V
          </span>
          <span className="font-semibold tracking-tight text-brand">VASP</span>
        </Link>

        <div className="ml-auto flex items-center gap-2">
          {loading ? (
            <span className="h-5 w-16 animate-pulse rounded-full bg-border" />
          ) : (
            <TierBadge tier={tier} />
          )}

          <ThemeToggle />

          <Link
            to="/pay/scan"
            aria-label="Scan to pay"
            className="rounded-lg p-1.5 text-ink-muted transition hover:bg-surface-sunken hover:text-ink"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.8}
                d="M4 7V5a1 1 0 0 1 1-1h2m10 0h2a1 1 0 0 1 1 1v2M4 17v2a1 1 0 0 0 1 1h2m10 0h2a1 1 0 0 0 1-1v-2M4 12h16"
              />
            </svg>
          </Link>

          <Link
            to="/notifications"
            aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
            className="relative rounded-lg p-1.5 text-ink-muted transition hover:bg-surface-sunken hover:text-ink"
          >
            <BellIcon className="h-5 w-5" />
            {unread > 0 ? (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-nano font-medium text-white">
                {unread > 9 ? '9+' : unread}
              </span>
            ) : null}
          </Link>

          <Link
            to="/profile"
            aria-label="Profile"
            className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-medium text-white transition hover:bg-primary-light"
          >
            {/* Deliberately not the participant's initials: a name in the top bar of a shared screen
                is a small privacy leak for no benefit. */}
            <ProfileIcon className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </header>
  );
}

// ============================================================================
// Desktop sidebar
// ============================================================================

/** Web: a grouped left sidebar (UF_00 section 4.4, 1024px and up). */
function Sidebar() {
  const { pathname } = useLocation();

  return (
    <nav className="hidden w-sidebar shrink-0 border-r border-border lg:block">
      <div className="sticky top-topbar max-h-[calc(100vh-3.5rem)] overflow-y-auto p-3">
        {NAV.map((group, gi) => (
          <div key={gi} className={gi > 0 ? 'mt-5' : ''}>
            {group.label ? (
              <p className="px-3 pb-1.5 text-micro font-semibold uppercase tracking-wider text-ink-subtle">
                {group.label}
              </p>
            ) : null}
            <ul className="space-y-1">
              {group.items.map((d) => {
                const active = isActive(pathname, d.href);
                return (
                  <li key={d.href}>
                    <Link
                      to={d.href}
                      aria-current={active ? 'page' : undefined}
                      className={`relative flex items-center gap-3 rounded-field px-3 py-2 text-sm transition ${
                        active
                          ? 'bg-primary-tint font-medium text-brand'
                          : 'text-ink-muted hover:bg-surface-sunken hover:text-ink'
                      }`}
                    >
                      {active ? (
                        <span
                          aria-hidden
                          className="absolute inset-y-1.5 left-0 w-1 rounded-r-full bg-primary"
                        />
                      ) : null}
                      <d.icon className="h-5 w-5 shrink-0" />
                      {d.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </nav>
  );
}

// ============================================================================
// Mobile tab bar + More sheet
// ============================================================================

function TabBar() {
  const { pathname } = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  // Any More destination active counts the More tab as active, so the current section is never
  // orphaned just because it lives behind the sheet.
  const moreActive = MORE_ITEMS.some((d) => isActive(pathname, d.href));

  return (
    <>
      <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-surface/95 shadow-[0_-2px_12px_rgba(15,26,21,0.06)] backdrop-blur lg:hidden">
        <ul className="mx-auto flex max-w-lg items-end justify-around px-2 pb-[env(safe-area-inset-bottom)]">
          {MOBILE_TABS.map((d) => {
            const active = isActive(pathname, d.href);

            if (d.href === PAY.href) {
              return (
                <li key={d.href} className="-mt-5">
                  <Link
                    to={d.href}
                    aria-current={active ? 'page' : undefined}
                    className="flex flex-col items-center gap-1"
                  >
                    <span
                      className={`flex h-12 w-12 items-center justify-center rounded-full shadow-lifted ring-4 ring-surface transition ${active ? 'bg-primary-dark' : 'bg-primary'}`}
                    >
                      <d.icon className="h-6 w-6 text-white" />
                    </span>
                    <span
                      className={`pb-1.5 text-nano ${active ? 'font-medium text-brand' : 'text-ink-muted'}`}
                    >
                      {d.label}
                    </span>
                  </Link>
                </li>
              );
            }

            return (
              <li key={d.href}>
                <Link
                  to={d.href}
                  aria-current={active ? 'page' : undefined}
                  className={`flex flex-col items-center gap-1 px-2 py-2 transition ${active ? 'text-brand' : 'text-ink-muted'}`}
                >
                  <d.icon className="h-5 w-5" />
                  <span className={`text-nano ${active ? 'font-medium' : ''}`}>{d.label}</span>
                </Link>
              </li>
            );
          })}

          {/* The More tab opens a sheet with the deeper areas. */}
          <li>
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={moreOpen}
              className={`flex flex-col items-center gap-1 px-2 py-2 transition ${moreActive ? 'text-brand' : 'text-ink-muted'}`}
            >
              <MoreIcon className="h-5 w-5" />
              <span className={`text-nano ${moreActive ? 'font-medium' : ''}`}>More</span>
            </button>
          </li>
        </ul>
      </nav>

      {/* The shared Sheet, not a bespoke re-implementation: it brings Escape-to-close, scroll-lock, a
          focus trap and focus restore that the hand-rolled version lacked. */}
      <Sheet open={moreOpen} onClose={() => setMoreOpen(false)} title="More">
        <div className="grid grid-cols-3 gap-2">
          {MORE_ITEMS.map((d) => {
            const active = isActive(pathname, d.href);
            return (
              <Link
                key={d.href}
                to={d.href}
                onClick={() => setMoreOpen(false)}
                aria-current={active ? 'page' : undefined}
                className={`flex flex-col items-center gap-2 rounded-card border p-3 text-center transition ${
                  active
                    ? 'border-primary/30 bg-primary-tint text-brand'
                    : 'border-border bg-surface text-ink-muted hover:bg-surface-sunken hover:text-ink'
                }`}
              >
                <d.icon className="h-6 w-6" />
                <span className="text-xs font-medium">{d.label}</span>
              </Link>
            );
          })}
        </div>
      </Sheet>
    </>
  );
}

// ============================================================================
// Shell
// ============================================================================

/**
 * The shell every signed-in panel sits inside.
 *
 * The app frame is FULL BLEED: the sidebar sits flush left and the main column takes the rest of
 * the width. It was once capped at max-w-content and centred, which on a wide screen left the
 * sidebar floating in the middle of the page with empty margins on both sides.
 * phone-width strip. The bottom padding on mobile clears the tab bar: without it the tab bar covers
 * the last row of a list, and the one row a user cannot see is the one they are looking for.
 */
export function AppShell({ children }: { children: React.ReactNode }) {
  return (
    <>
      <TopBar />
      <div className="flex">
        <Sidebar />
        <main className="min-w-0 flex-1 px-4 pb-28 pt-5 lg:px-8 lg:pb-10 2xl:px-12">{children}</main>
      </div>
      <TabBar />
    </>
  );
}
